// ABOUTME: One place that turns any failure (Apollo, native bridge, plain Error) into the text a user may see.
// ABOUTME: Server error codes translate through the gqlErrors namespace; anything unknown becomes a generic message.
import type { TFunction } from "i18next"
import i18next from "i18next"
import { CombinedGraphQLErrors, ServerError, ServerParseError } from "@apollo/client/errors"

const SERVER_ERROR_KEY = "errors.SERVER_ERROR"
const NETWORK_ERROR_KEY = "errors.networkError"
const UNKNOWN_ERROR_KEY = "errors.unknownError"

const asKey = (value: unknown): string | null => (typeof value === "string" && i18next.exists(value) ? value : null)

/**
 * The translation key for `error`, or null when there is none:
 * - a GraphQL error's `extensions.code`, else its `message` (the server sends codes like `ErrFolderNestingTooDeep`
 *   as the message, and src/locales/gqlErrors translates them);
 * - an object's `code` or `message` (NativeBridgeError, `throw new Error("ErrX")`).
 */
export const errorTranslationKey = (error: unknown): string | null => {
  if (CombinedGraphQLErrors.is(error)) {
    for (const graphQLError of error.errors) {
      const key = asKey(graphQLError.extensions?.code) ?? asKey(graphQLError.message)
      if (key) return key
    }
    return null
  }
  if (typeof error === "object" && error !== null) {
    const { code, message } = error as { code?: unknown; message?: unknown }
    return asKey(code) ?? asKey(message)
  }
  return asKey(error)
}

/**
 * The server's code for a refused operation — `extensions.code`, else the message, which is the
 * code itself and the only place `ErrSystemErrors` carries it — or null when no server answered.
 * For deciding what to do; what to show is userErrorMessage's job.
 */
export const serverErrorCode = (error: unknown): string | null => {
  if (!CombinedGraphQLErrors.is(error)) return null
  const [first] = error.errors
  const code = first?.extensions?.code
  return typeof code === "string" ? code : (first?.message ?? null)
}

const isNetworkFailure = (error: unknown): boolean =>
  ServerError.is(error) ||
  ServerParseError.is(error) ||
  (error instanceof TypeError && /fetch|network|load failed/i.test(error.message))

/**
 * What to show the user for `error`. Never the raw technical text: a known code is translated, a failed
 * request says so, and everything else is the generic message.
 */
export const userErrorMessage = (error: unknown, t: TFunction): string => {
  const key = errorTranslationKey(error)
  if (key) return t(key)
  if (isNetworkFailure(error)) return t(NETWORK_ERROR_KEY)
  if (CombinedGraphQLErrors.is(error)) return t(SERVER_ERROR_KEY)
  return t(UNKNOWN_ERROR_KEY)
}
